<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="max-w-6xl mx-auto">
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Berita', href: route('berita.index') },
            { name: 'Tambah Berita' },
          ]"
        />

        <form class="space-y-6" @submit.prevent="submit">
          <!-- Title card (full width, prominent) -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h2
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-plus sm:mr-3"></i>
                Tambah Berita
              </h2>
            </div>
            <div class="p-4 sm:p-6">
              <InputField
                label="Judul Berita"
                icon="pen"
                v-model="form.title"
                placeholder="Masukkan judul berita..."
              />
              <InputError :message="form.errors.title" class="mt-2" />
            </div>
          </div>

          <!-- Main editor + metadata sidebar -->
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <!-- Editor (main) -->
            <div class="lg:col-span-8">
              <div
                class="p-4 bg-white border border-gray-200 shadow-lg sm:p-6 rounded-xl"
              >
                <InputLabel value="Konten Berita" />
                <div class="mt-2 quill-editor-wrapper">
                  <QuillEditor
                    ref="quillRef"
                    v-model="form.content"
                    :error="form.errors.content"
                    :modules="modules"
                    class="custom-quill-editor"
                  />
                </div>
                <InputError :message="form.errors.content" class="mt-2" />
              </div>
            </div>

            <!-- Sidebar (metadata) -->
            <div class="lg:col-span-4">
              <div class="space-y-6 lg:sticky lg:top-24">
                <!-- Publikasi -->
                <div
                  class="bg-white border border-gray-200 shadow-sm rounded-xl"
                >
                  <div
                    class="px-4 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50"
                  >
                    <h3
                      class="flex items-center text-sm font-semibold text-gray-800"
                    >
                      <i
                        class="mr-2 text-indigo-500 fa-solid fa-paper-plane"
                      ></i>
                      Publikasi
                    </h3>
                  </div>
                  <div class="p-4 space-y-4 sm:p-5">
                    <div>
                      <InputLabel for="publish_date" value="Tanggal Publish" />
                      <input
                        v-model="form.publish_date"
                        type="date"
                        id="publish_date"
                        name="publish_date"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      />
                      <InputError
                        :message="form.errors.publish_date"
                        class="mt-2"
                      />
                    </div>

                    <div>
                      <InputLabel for="published" value="Status Publish" />
                      <select
                        v-model="form.published"
                        id="published"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                        :class="{
                          'border-red-500 focus:ring-red-500 focus:border-red-500':
                            form.errors.published,
                        }"
                      >
                        <option value="">Pilih Status</option>
                        <option value="0">Draft</option>
                        <option value="1">Publish</option>
                      </select>
                      <InputError
                        :message="form.errors.published"
                        class="mt-2"
                      />
                    </div>

                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Simpan Berita" }}
                    </PrimaryBtn>
                  </div>
                </div>

                <!-- Kategori / Tags -->
                <div
                  class="bg-white border border-gray-200 shadow-sm rounded-xl"
                >
                  <div
                    class="px-4 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50"
                  >
                    <h3
                      class="flex items-center text-sm font-semibold text-gray-800"
                    >
                      <i class="mr-2 text-indigo-500 fa-solid fa-tags"></i>
                      Kategori / Tags
                    </h3>
                  </div>
                  <div class="p-4 sm:p-5">
                    <Multiselect
                      v-model="form.tags"
                      :options="props.allTags"
                      :multiple="true"
                      :taggable="true"
                      track-by="name"
                      label="name"
                      placeholder="Pilih atau tambah tag"
                      tag-placeholder="Tambah tag"
                      @tag="addTag"
                      class="w-full modern-multiselect"
                    />
                    <InputError :message="form.errors.tags" class="mt-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>
  </Container>
</template>
<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import QuillEditor from "@/Components/QuillEditor.vue";
import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";
import { router, useForm, usePage } from "@inertiajs/vue3";
import "vue-multiselect/dist/vue-multiselect.min.css";

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  allTags: {
    type: Array,
    required: true,
  },
});

//End of Pagination
function addTag(newTag) {
  const exists =
    form.tags.some((tag) => tag.name === newTag) ||
    props.allTags.some((tag) => tag.name === newTag);

  if (!exists) {
    form.tags.push({ name: newTag });
  }
}

const form = useForm({
  id: null,
  title: "",
  content: "",
  publish_date: "",
  published: "0", // Default ke Draft untuk menghindari error validasi
  tags: [],
});

// handleFileChange dihapus, upload gambar hanya via QuillEditor
onMounted(() => {
  const page = usePage();
  if (page.props.flash && page.props.flash.success) {
    Swal.fire({
      title: "Berhasil!",
      text: page.props.flash.success,
      icon: "success",
      confirmButtonText: "OK",
    });
  }

  // Fix cursor issue in QuillEditor with safer approach
  setTimeout(() => {
    if (quillRef.value) {
      try {
        let quill = null;
        if (typeof quillRef.value.getQuill === "function") {
          quill = quillRef.value.getQuill();
        } else if (quillRef.value.editor) {
          quill = quillRef.value.editor;
        }

        if (quill) {
          // Simple approach: just override the problematic keyboard binding
          quill.keyboard.addBinding({
            key: "Enter",
            handler: function (range, context) {
              // Use default Quill behavior but ensure proper cursor position
              return true;
            },
          });

          console.log("QuillEditor initialized successfully");
        }
      } catch (error) {
        console.log("QuillEditor initialization skipped:", error.message);
      }
    }
  }, 2000);
});

const fileInput = ref(null);
const quillRef = ref(null);
// Untuk akses instance CKEditor:
// ckeditorRef.value?.editorInstance

// Function to resize image to 800x600px while maintaining aspect ratio
function resizeImage(file, maxWidth = 800, maxHeight = 600) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(resolve, file.type, 0.8);
    };

    img.src = URL.createObjectURL(file);
  });
}

// Custom image upload handler for Quill with auto resize to 800x600px
function imageHandler() {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;

    try {
      // Resize image to 800x600px before uploading
      const resizedFile = await resizeImage(file, 800, 600);

      const formData = new FormData();
      formData.append("image", resizedFile, file.name);

      const response = await axios.post("/berita/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = response.data.url;
      await nextTick();
      let quill = null;
      if (quillRef.value && typeof quillRef.value.getQuill === "function") {
        quill = quillRef.value.getQuill();
      } else if (quillRef.value && quillRef.value.editor) {
        quill = quillRef.value.editor;
      } else if (
        window &&
        window.Quill &&
        document.querySelector(".ql-editor")
      ) {
        // fallback: try to get Quill instance from DOM
        quill = window.Quill.find(document.querySelector(".ql-editor"));
      }
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(range ? range.index : 0, "image", url);
      } else {
        Swal.fire({
          title: "Gagal upload gambar",
          text: "Editor tidak ditemukan.",
          icon: "error",
        });
      }
    } catch (e) {
      Swal.fire({
        title: "Gagal upload gambar",
        text: "Terjadi kesalahan saat upload gambar.",
        icon: "error",
      });
    }
  };
}

// Ganti ke quill-resize-image
import QuillResizeImage from "quill-resize-image";
import Quill from "quill";

Quill.register("modules/resizeImage", QuillResizeImage);

const modules = {
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    handlers: {
      image: imageHandler,
    },
  },
  resizeImage: {
    displaySize: true,
    modules: ["Resize", "DisplaySize", "Toolbar"],
    handleStyles: {
      backgroundColor: "#007cff",
      border: "2px solid #fff",
      borderRadius: "50%",
      width: "12px",
      height: "12px",
    },
    defaultSize: {
      width: 800,
      height: 600,
    },
  },
};
// (no changes above)

const submit = async () => {
  // Reset errors
  form.errors = {};

  // Validasi frontend sebelum kirim
  let hasErrors = false;
  const errors = {};

  if (!form.title.trim()) {
    errors.title = "Judul harus diisi.";
    hasErrors = true;
  }

  if (!form.content.trim()) {
    errors.content = "Content harus diisi.";
    hasErrors = true;
  }

  if (!form.publish_date) {
    errors.publish_date = "Tanggal publikasi harus diisi.";
    hasErrors = true;
  }

  if (!form.published || form.published === "") {
    errors.published = "Status publikasi harus diisi.";
    hasErrors = true;
  }

  if (hasErrors) {
    form.errors = errors;
    Swal.fire({
      title: "Validasi Error",
      text: "Mohon lengkapi semua field yang diperlukan",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  // Pastikan form.tags berisi array nama tag
  const selectedTags = form.tags.map((tag) => tag.name || tag);
  const formData = new FormData();
  formData.append("title", form.title);
  formData.append("content", form.content);
  formData.append("publish_date", form.publish_date);
  formData.append("published", form.published);

  // Kirim tags sebagai array string
  if (Array.isArray(selectedTags)) {
    selectedTags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
  }

  // Reset errors
  form.errors = {};

  try {
    // Show loading
    Swal.fire({
      title: "Menyimpan...",
      text: "Sedang menyimpan berita",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    await axios.post(route("berita.store"), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    Swal.fire({
      title: "Berhasil!",
      text: "Berita berhasil disimpan",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      router.visit(route("berita.index"), {
        preserveScroll: true,
        preserveState: true,
      });
      // Reset form after successful submission
      form.reset();
      form.tags = [];
      form.title = "";
      form.content = "";
      form.publish_date = "";
      form.published = "0"; // Reset ke default Draft

      // Reset QuillEditor if available
      if (quillRef.value) {
        try {
          if (quillRef.value.setHTML) {
            quillRef.value.setHTML("");
          }
          if (quillRef.value.editor && quillRef.value.editor.setContents) {
            quillRef.value.editor.setContents([]);
          }
        } catch (error) {
          console.log("QuillEditor reset skipped:", error.message);
        }
      }
    });
  } catch (error) {
    if (error.response && error.response.status === 422) {
      form.errors = error.response.data.errors;
      Swal.fire({
        title: "Validasi Gagal!",
        text: "Mohon periksa kembali data yang diinputkan",
        icon: "error",
        confirmButtonText: "OK",
      });
    } else {
      Swal.fire({
        title: "Error!",
        text: "Terjadi kesalahan saat menyimpan berita",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }
};
</script>
<style scoped>
.quill-editor-wrapper {
  width: 100%;
}
.custom-quill-editor {
  min-height: 600px;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  padding: 20px;
}
.custom-quill-editor .ql-container {
  min-height: 500px;
  font-size: 1.1rem;
}
.custom-quill-editor .ql-toolbar {
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}
.custom-quill-editor .ql-editor {
  line-height: 1.6;
  padding: 12px 15px;
}
.custom-quill-editor .ql-editor p {
  margin-bottom: 0.5em;
  line-height: inherit;
}
.custom-quill-editor .ql-editor:focus {
  outline: none;
}
.custom-quill-editor .ql-editor img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  display: block;
  margin: 10px auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Multiselect styling */
.multiselect {
  min-height: 56px;
  border: 1px solid #d1d5db;

  font-size: 1.125rem;
}

.multiselect__tags {
  border: none;

  padding: 12px 16px;
  min-height: 56px;
}

.multiselect__input {
  font-size: 1.125rem;
  padding: 4px 0;
}

.multiselect__tag {
  background: #3b82f6;

  padding: 4px 8px;
  margin: 2px;
}

.multiselect__option--highlight {
  background: #3b82f6;
}

.multiselect__option--selected.multiselect__option--highlight {
  background: #1d4ed8;
}

/* Image resize handles styling */
.custom-quill-editor .ql-editor .ql-image-resize {
  position: relative;
  display: inline-block;
}

.custom-quill-editor .ql-editor .ql-image-resize img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
}

/* Resize handles */
.custom-quill-editor .ql-editor .ql-image-resize .ql-resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #007cff;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: nw-resize;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-nw {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-ne {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-sw {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.custom-quill-editor
  .ql-editor
  .ql-image-resize
  .ql-resize-handle.ql-resize-handle-se {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}

/* Image toolbar */
.custom-quill-editor .ql-editor .ql-image-toolbar {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  border-radius: 4px;
  padding: 4px 8px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.custom-quill-editor .ql-editor .ql-image-toolbar button {
  background: none;
  border: none;
  color: #fff;
  font-size: 12px;
  padding: 2px 4px;
  margin: 0 2px;
  cursor: pointer;
  border-radius: 2px;
}

.custom-quill-editor .ql-editor .ql-image-toolbar button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Image alignment classes */
.custom-quill-editor .ql-editor img.ql-align-left {
  float: left;
  margin: 0 16px 16px 0;
}

.custom-quill-editor .ql-editor img.ql-align-right {
  float: right;
  margin: 0 0 16px 16px;
}

.custom-quill-editor .ql-editor img.ql-align-center {
  display: block;
  margin: 16px auto;
}

/* Display size tooltip */
.custom-quill-editor .ql-editor .ql-image-display-size {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 10;
}

/* Responsive behavior for images */
@media (max-width: 768px) {
  .custom-quill-editor .ql-editor img {
    width: 100% !important;
    height: auto !important;
  }
}

/* Mobile responsiveness */
@media (max-width: 1024px) {
  .custom-quill-editor {
    min-height: 400px;
  }

  .custom-quill-editor .ql-container {
    min-height: 300px;
  }

  .grid-cols-1.lg\\:grid-cols-2 {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

@media (max-width: 640px) {
  .custom-quill-editor {
    min-height: 300px;
    padding: 16px;
  }

  .custom-quill-editor .ql-container {
    min-height: 200px;
  }

  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
